import { v4 as uuid } from 'uuid';
import React from 'react';

export const canvasDraftSaveSignature = ({ documentIdentity = '', activeTabKey = '', content = '' } = {}) =>
  JSON.stringify([documentIdentity, activeTabKey, content]);

export const shouldAutoSaveCanvasDraft = ({ enabled = false, baselineContent, lastSavedSignature, ...draft } = {}) =>
  enabled && draft.content !== baselineContent && canvasDraftSaveSignature(draft) !== lastSavedSignature;

/** One document owns its writes; a newer queued draft replaces redundant work. */
export const createCanvasDraftSaveQueue = ({ onSave, onAccepted, createRequestId = () => uuid() } = {}) => {
  const requests = new Map();
  let active = null;
  let pending = null;
  let disposed = false;
  const cancelled = () => new DOMException('Draft save was superseded or disposed', 'AbortError');
  const run = async (request) => {
    active = request;
    try {
      const result = await onSave(request.content, { ...request.options, requestId: request.id, signal: request.controller.signal });
      request.controller.signal.throwIfAborted();
      await onAccepted?.(request.content, result, request.options);
      requests.delete(request.content);
      request.resolve(result);
    } catch (error) {
      request.reject(error);
    } finally {
      active = null;
      const next = pending;
      pending = null;
      if (next && !disposed) void run(next);
    }
  };
  const createRequest = (content, options) => {
    let resolve;
    let reject;
    const promise = new Promise((yes, no) => { resolve = yes; reject = no; });
    const id = requests.get(content) ?? createRequestId();
    requests.set(content, id);
    return { content, options, id, promise, resolve, reject, controller: new AbortController() };
  };
  const replacePending = (request) => { pending?.reject(cancelled()); pending = request; };
  const reuseRequest = (request, options) => { request.options = { ...request.options, ...options }; return request.promise; };
  const matchesActive = (content) => active?.content === content && !pending;
  const save = (content, options = {}) => {
    if (disposed) return Promise.reject(cancelled());
    if (matchesActive(content)) return reuseRequest(active, options);
    if (pending?.content === content) return reuseRequest(pending, options);
    const request = createRequest(content, options);
    if (!active) { void run(request); return request.promise; }
    replacePending(request);
    return request.promise;
  };
  const dispose = () => {
    disposed = true;
    active?.controller.abort();
    pending?.reject(cancelled());
    pending = null;
    requests.clear();
  };
  return { save, dispose };
};

const autosaveDefaults = { documentIdentity: '', activeTabKey: '', baselineContent: '', canvasDraftEditing: false, canvasDraftEditorValue: '', debounceMs: 1200, enabled: false };
export const useCanvasDraftAutosave = (hookOptions = {}) => {
  const { documentIdentity, activeTabKey, baselineContent, canvasDraftEditing, canvasDraftEditorValue, debounceMs, enabled, onAutosave, onAccepted, onError } = { ...autosaveDefaults, ...hookOptions };
  const callbacks = React.useRef({ onAutosave, onAccepted, onError });
  callbacks.current = { onAutosave, onAccepted, onError };
  const lastSaved = React.useRef('');
  const queueRef = React.useRef(null);
  const markSaved = React.useCallback((tabKey, content) => {
    lastSaved.current = canvasDraftSaveSignature({ documentIdentity, activeTabKey: tabKey, content });
  }, [documentIdentity]);

  React.useLayoutEffect(() => {
    markSaved(activeTabKey, baselineContent);
  }, [activeTabKey, baselineContent, markSaved]);

  React.useLayoutEffect(() => {
    const queue = createCanvasDraftSaveQueue({
      onSave: (content, options) => callbacks.current.onAutosave(content, options),
      onAccepted: (content, result, options) => {
        markSaved(activeTabKey, content);
        callbacks.current.onAccepted?.(content, result, options);
      },
    });
    queueRef.current = queue;
    return () => { queue.dispose(); queueRef.current = null; };
    // A revision change must not dispose this document's in-flight edit queue.
  }, [documentIdentity, activeTabKey, markSaved]);

  const save = React.useCallback((content, options) => {
    if (!queueRef.current) return Promise.reject(new Error('Draft save owner is unavailable'));
    return queueRef.current.save(content, options);
  }, []);

  React.useEffect(() => {
    if (!canvasDraftEditing || !shouldAutoSaveCanvasDraft({ documentIdentity, activeTabKey, content: canvasDraftEditorValue, baselineContent, enabled, lastSavedSignature: lastSaved.current })) return undefined;
    const timer = setTimeout(() => {
      void save(canvasDraftEditorValue, { closeEditor: false, syncEditorValue: false }).catch((error) => {
        if (error.name !== 'AbortError') callbacks.current.onError?.(error);
      });
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [documentIdentity, activeTabKey, baselineContent, canvasDraftEditing, canvasDraftEditorValue, debounceMs, enabled, save]);

  return React.useMemo(() => Object.assign(markSaved, { save }), [markSaved, save]);
};
export default useCanvasDraftAutosave;
